import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import Icon from './Icon';
import Avatar from './Avatar';
import '../styles/Modal.css';
import '../styles/RecipientSelectorModal.css';

/**
 * RecipientSelectorModal - Modal for selecting recipients when sending items to clients
 *
 * Props:
 * - isOpen: boolean - Whether the modal is open
 * - onClose: callback - Called when modal is closed
 * - onConfirm: callback(recipientIds) - Called when user confirms selection
 * - itemType: 'ideas' | 'content-drafts' - Type of item being sent
 * - itemId: string - ID of the ideas document or draft
 * - itemCount: number - Number of items being sent (for display)
 */
const RecipientSelectorModal = ({
  isOpen,
  onClose,
  onConfirm,
  itemType,
  itemId,
  itemCount = 1
}) => {
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [brandName, setBrandName] = useState('');

  useEffect(() => {
    if (isOpen && itemId) {
      fetchRecipients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, itemId, itemType]);

  const fetchRecipients = async () => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = itemType === 'ideas'
        ? `/api/ideas/${itemId}/available-recipients`
        : `/api/content-drafts/${itemId}/available-recipients`;

      const response = await axios.get(endpoint);

      if (response.data.success) {
        setRecipients(response.data.recipients);
        setBrandName(response.data.brand_name);

        // Auto-select Brand Owner (they're always included)
        const autoSelected = response.data.recipients
          .filter(r => r.auto_recipient)
          .map(r => r.user_id);
        setSelectedIds(autoSelected);
      } else {
        setError('Failed to load recipients');
      }
    } catch (err) {
      console.error('Error fetching recipients:', err);
      setError(err.response?.data?.error || 'Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRecipient = (userId, isAutoRecipient) => {
    // Don't allow deselecting auto-recipients (Brand Owner)
    if (isAutoRecipient) return;

    setSelectedIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleConfirm = () => {
    if (selectedIds.length === 0) {
      setError('Please select at least one recipient');
      return;
    }

    onConfirm(selectedIds);
    handleClose();
  };

  const handleClose = () => {
    setSelectedIds([]);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const selectedCount = selectedIds.length;
  const itemText = itemCount === 1 ? (itemType === 'ideas' ? 'idea' : 'draft') : `${itemCount} ${itemType === 'ideas' ? 'ideas' : 'drafts'}`;

  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="modal-container modal-medium recipient-selector-modal">
        {/* Header */}
        <div className="modal-header">
          <h3 className="modal-title">
            Select Recipients
          </h3>
          <button className="modal-close-btn" onClick={handleClose} aria-label="Close" />
        </div>

        {/* Body */}
        <div className="modal-body recipient-modal-body">
          {loading ? (
            <div className="recipient-modal-loading">
              <Icon name="loading" />
              <p>Loading recipients...</p>
            </div>
          ) : error ? (
            <div className="recipient-modal-error">
              <Icon name="close-x" />
              <p>{error}</p>
            </div>
          ) : (
            <>
              <p className="recipient-modal-subtitle">
                Who should receive {itemText} for <strong>{brandName}</strong>?
              </p>

              <div className="recipient-list">
                {recipients.map((recipient) => {
                  const isSelected = selectedIds.includes(recipient.user_id);
                  const isAutoRecipient = recipient.auto_recipient;
                  const isCasper = recipient.is_casper;

                  return (
                    <div
                      key={recipient.user_id}
                      className={`recipient-item ${isSelected ? 'selected' : ''} ${isAutoRecipient ? 'auto-recipient' : ''}`}
                      onClick={() => handleToggleRecipient(recipient.user_id, isAutoRecipient)}
                    >
                      <div className="recipient-checkbox">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleRecipient(recipient.user_id, isAutoRecipient)}
                          disabled={isAutoRecipient}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      <Avatar
                        user={{ picture: recipient.profile_image, name: recipient.name }}
                        size="sm"
                        className="recipient-avatar"
                      />

                      <div className="recipient-info">
                        <div className="recipient-name">
                          {recipient.name}
                          {isCasper && (
                            <span className="recipient-badge recipient-badge-casper">
                              <Icon name="robot" />
                              AI Bot
                            </span>
                          )}
                          {recipient.type === 'org_owner' && (
                            <span className="recipient-badge recipient-badge-org-owner">
                              <Icon name="building" />
                              Org Owner
                            </span>
                          )}
                          {recipient.type === 'brand_owner' && (
                            <span className="recipient-badge recipient-badge-owner">
                              <Icon name="star" />
                              Brand Owner
                            </span>
                          )}
                          {recipient.status === 'pending' && (
                            <span className="recipient-badge recipient-badge-pending">
                              <Icon name="clock" />
                              Pending Invitation
                            </span>
                          )}
                        </div>
                        <div className="recipient-email">{recipient.email}</div>
                        {isAutoRecipient && (
                          <div className="recipient-auto-note">
                            Always receives items
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && (
          <div className="modal-footer recipient-modal-footer">
            <div className="recipient-modal-selected-count">
              {selectedCount} {selectedCount === 1 ? 'recipient' : 'recipients'} selected
            </div>
            <div className="recipient-modal-actions">
              <button
                className="modal-btn modal-btn-cancel"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                className="modal-btn modal-btn-confirm"
                onClick={handleConfirm}
                disabled={selectedIds.length === 0}
              >
                Send to {selectedCount} {selectedCount === 1 ? 'Recipient' : 'Recipients'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default RecipientSelectorModal;
